import React, {useEffect, useState, useCallback} from "react";
import {
  createTokenBucketAccess,
  deleteTokenBucketAccess,
  fetchAllTokenBucketAccess, fetchAllTokenBucketAccessTokensList, fetchAllValidTokens,
  fetchTokenBucketAccessBucketsList, fetchTokensByUserId,
  fetchUserTokenBucketAccess, fetchUserTokenBucketAccessTokensList,
  updateTokenBucketAccess,
} from "../api";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {Button} from "react-bootstrap";
import {formatAllBucketAccessRoles, getErrorMessage} from "../utils";
import DeleteConfirmation from "./DeleteConfirmation";
import {
  PORTAL_BUCKET_CHIP,
  PORTAL_CHIP,
  PORTAL_GRADIENT,
  PORTAL_HEADER_ICON,
  PORTAL_PRIMARY,
  STATUS_CHIP_COLORS
} from "./AdminStyles";

const STATUS_COLORS = {
  Active: STATUS_CHIP_COLORS.active,
  Rotated: STATUS_CHIP_COLORS.rotated,
  Revoked: STATUS_CHIP_COLORS.revoked,
  Expired: STATUS_CHIP_COLORS.expired,
};

export default function TokenBucketAccessCRUD({admin, userId, appAdmin, teamAdmin}) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isErrorData, setIsErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isPendingData, setIsPendingData] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [toast, setToast] = useState({open: false, severity: "info", message: ""});
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [tokensList, setTokensList] = useState([]);
  const [bucketsList, setBucketsList] = useState([]);
  const [editDialog, setEditDialog] = useState({open: false, mode: "create", data: null});
  const [formValues, setFormValues] = useState({token: null, bucket_names: []});
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  useEffect(() => {
    setPage(0);
  }, [filteredData]);
  
  const showToast = (severity, message) => setToast({open: true, severity, message});
  const closeToast = () => setToast((t) => ({...t, open: false}));
  
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredData(
        data.filter(
          (item) =>
            item.token_name?.toLowerCase().includes(lower) ||
            (item.bucket_names || []).some((bucket) => bucket.toLowerCase().includes(lower))
        )
      );
    }
  }, [searchTerm, data]);
  
  function validateData(values) {
    const errors = {};
    if (editDialog.mode === "create" && !values?.token?.token_id) {
      errors.token = "Token is required";
    }
    if (!values?.bucket_names?.length) {
      errors.bucket_names = "At least one bucket must be selected";
    }
    return errors;
  }
  
  const fetchRecordsData = useCallback(async () => {
    setIsErrorData(false);
    setBannerError("");
    try {
      const response = await (admin ? fetchAllTokenBucketAccess() : teamAdmin ? fetchUserTokenBucketAccess(userId) : fetchAllTokenBucketAccess());
      const formatted = formatAllBucketAccessRoles(response?.data ?? []);
      setData(formatted);
    } catch (error) {
      setIsErrorData(true);
      setData([]);
      const msg = getErrorMessage(error, "Error loading data");
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsLoadingData(false);
    }
  }, []);
  
  const fetchTokensList = useCallback(async () => {
    try {
      const response = await (admin ? fetchAllTokenBucketAccessTokensList() : teamAdmin ? fetchUserTokenBucketAccessTokensList(userId) : fetchAllTokenBucketAccessTokensList());
      setTokensList(response?.data ?? []);
    } catch (error) {
      setTokensList([]);
      const msg = getErrorMessage(error, "Error loading tokens list");
      setBannerError(msg);
      showToast("error", msg);
    }
  }, []);
  
  const fetchBucketsList = useCallback(async (tokenId) => {
    try {
      const response = await fetchTokenBucketAccessBucketsList(tokenId);
      setBucketsList(response?.data ?? []);
    } catch (error) {
      setBucketsList([]);
      const msg = getErrorMessage(error, "Error loading buckets list");
      setBannerError(msg);
      showToast("error", msg);
    }
  }, []);
  
  useEffect(() => {
    fetchRecordsData();
    fetchTokensList();
  }, [fetchRecordsData, fetchTokensList]);
  
  const openCreateDialog = async () => {
    setFormValues({token: null, bucket_names: []});
    setValidationErrors({});
    setBannerError("");
    setEditDialog({open: true, mode: "create", data: null});
    await fetchTokensList();
    await fetchBucketsList(null);
  };
  
  const openEditDialog = async (item) => {
    setFormValues({
      token: {token_id: item.token_id, token_name: item.token_name},
      bucket_names: item.bucket_names || [],
    });
    setValidationErrors({});
    setBannerError("");
    setEditDialog({open: true, mode: "edit", data: item});
    await fetchBucketsList(item.token_id);
  };
  
  const closeDialog = () => {
    setEditDialog({open: false, mode: "create", data: null});
    setValidationErrors({});
  };
  
  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({...prev, [field]: value}));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({...prev, [field]: undefined}));
    }
  };
  
  const handleTokenChange = async (token) => {
    handleFormChange("token", token);
    handleFormChange("bucket_names", []);
    await fetchBucketsList(token?.token_id);
  };
  
  const handleSave = async () => {
    const errors = validateData(formValues);
    if (Object.values(errors).some(Boolean)) {
      setValidationErrors(errors);
      showToast("error", "Please fix the highlighted fields.");
      return;
    }
    setIsPendingData(true);
    setBannerError("");
    try {
      const tokenId = formValues.token.token_id;
      const payload = {bucket_names: formValues.bucket_names};
      if (editDialog.mode === "create") {
        await createTokenBucketAccess(tokenId, payload);
      } else {
        await updateTokenBucketAccess(tokenId, payload);
      }
      await fetchRecordsData();
      await fetchTokensList();
      closeDialog();
      showToast("success", editDialog.mode === "create" ? "Bucket access role created successfully." : "Bucket access role updated successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsPendingData(false);
    }
  };
  
  const requestDelete = (item) => {
    setBannerError("");
    setDeleteTargetRow(item);
  };
  
  const handleCancelDelete = () => setDeleteTargetRow(null);
  
  const handleConfirmDelete = async () => {
    if (!deleteTargetRow) return;
    try {
      await deleteTokenBucketAccess(deleteTargetRow.token_id);
      await fetchRecordsData();
      await fetchTokensList();
      showToast("success", "Bucket access deleted successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setDeleteTargetRow(null);
    }
  };
  
  const getBucketOptions = () => {
    const selected = formValues.bucket_names || [];
    const missing = selected.filter((bucket) => !bucketsList.includes(bucket));
    return [...missing, ...bucketsList];
  };
  
  const isEditable = (item) => (admin || teamAdmin) && item.token_status !== "Expired" && item.token_status !== "Revoked";
  
  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4].map((i) => (
        <TableRow key={i}>
          <TableCell><Skeleton variant="text" width={140}/></TableCell>
          <TableCell><Skeleton variant="text" width={90}/></TableCell>
          <TableCell><Skeleton variant="text" width="80%"/></TableCell>
          {(admin || teamAdmin) && <TableCell><Skeleton variant="text" width={80}/></TableCell>}
        </TableRow>
      ))}
    </TableBody>
  );
  
  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={(admin || teamAdmin) ? 6 : 5} sx={{py: 8, textAlign: "center"}}>
        <ListAltIcon sx={{fontSize: 48, color: "#bdbdbd", mb: 1}}/>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Token Bucket Access Found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          {searchTerm ? "No results match your search criteria." : "Get started by configuring bucket access for a token."}
        </Typography>
        {(admin || teamAdmin) && !searchTerm && (
          <Button variant="primary" onClick={openCreateDialog}>
            <AddCircleIcon fontSize="small" style={{marginRight: 8}}/>
            Configure Bucket Access
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
  
  return (
    <Box sx={{maxWidth: 1400, mx: "auto", p: 1}}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background: PORTAL_GRADIENT,
          color: "white",
        }}
      >
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VpnKeyIcon sx={{fontSize: 28, color: PORTAL_HEADER_ICON}}/>
              <Typography variant="h6" fontWeight={700}>
                Bucket Access Roles Management
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{opacity: 0.9}}>
              Map buckets to the tokens which are allowed to download from them
            </Typography>
          </Box>
          {(admin || teamAdmin) && (
            <Button variant="light" onClick={openCreateDialog}
                    style={{display: "flex", alignItems: "center", gap: 8, fontWeight: 600}}>
              <AddCircleIcon fontSize="small"/>
              Create New
            </Button>
          )}
        </Stack>
      </Paper>
      
      {(bannerError || isErrorData) && (
        <Alert severity="error" sx={{mb: 2, borderRadius: 2}}>
          {bannerError || "Error loading data"}
        </Alert>
      )}
      
      <Paper elevation={0} sx={{p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <TextField
            placeholder="Search by token name or bucket name..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{minWidth: 400}}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon
                color="action"/></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}><CloseIcon
                    fontSize="small"/></IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredData.length} ${filteredData.length === 1 ? "record" : "records"}`}
                color="primary"
                variant="outlined"/>
        </Stack>
      </Paper>
      
      <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: "#f5f5f5"}}>
              <TableCell sx={{fontWeight: 700}}>Token Name</TableCell>
              <TableCell sx={{fontWeight: 700, width: 300}}>Buckets</TableCell>
              <TableCell sx={{fontWeight: 700}}>Created By</TableCell>
              <TableCell sx={{fontWeight: 700}}>Token Status</TableCell>
              <TableCell sx={{fontWeight: 700}}>Created At</TableCell>
              {(admin || teamAdmin) &&
                <TableCell sx={{fontWeight: 700, width: 120, textAlign: "center"}}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          {isLoadingData ? (
            <TableSkeleton/>
          ) : filteredData.length === 0 ? (
            <TableBody><EmptyState/></TableBody>
          ) : (
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow
                  key={item.token_id}
                  sx={{
                    "&:hover": {backgroundColor: "#fafafa"},
                    "&:nth-of-type(even)": {backgroundColor: "#fcfcfc"},
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={<VpnKeyIcon sx={{fontSize: 16}}/>}
                      label={item.token_name}
                      size="small"
                      sx={PORTAL_CHIP}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap", gap: 0.5}}>
                      {(item.bucket_names || []).map((bucket) => (
                        <Chip
                          key={bucket}
                          icon={<StorageIcon sx={{fontSize: 16}}/>}
                          label={bucket}
                          size="small"
                          sx={PORTAL_BUCKET_CHIP}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>{item.created_by}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.token_status}
                      size="small"
                      sx={{fontWeight: 600, ...(STATUS_COLORS[item.token_status] || {})}}
                    />
                  </TableCell>
                  <TableCell>{item.created_at}</TableCell>
                  {(admin || teamAdmin) && (
                    <TableCell sx={{textAlign: "center"}}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip
                          title={isEditable(item) ? "Edit" : "Token is expired or revoked"}>
                                                    <span>
                                                        <IconButton size="small" disabled={!isEditable(item)}
                                                                    onClick={() => openEditDialog(item)}
                                                                    sx={{color: PORTAL_PRIMARY}}>
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => requestDelete(item)}
                                      sx={{color: "#d32f2f"}}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        <Box sx={{display: "flex", justifyContent: "flex-end", mt: 1}}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                gap: 1,
                minHeight: 48,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                margin: 0,
                alignSelf: "center",
              },
              "& .MuiTablePagination-actions": {
                marginLeft: 1,
              },
            }}
          />
        </Box>
      </TableContainer>
      
      <Dialog open={editDialog.open} onClose={closeDialog} maxWidth="sm" fullWidth
              PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle
          sx={{background: PORTAL_GRADIENT, color: "white", py: 2}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {editDialog.mode === "create" ? <AddCircleIcon/> : <EditIcon/>}
            <Typography variant="h6" fontWeight={600}>
              {editDialog.mode === "create" ? "New Token Bucket Access" : `Edit - ${editDialog.data?.token_name}`}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt: 3, pb: 2}}>
          <Stack spacing={3} sx={{mt: 3}}>
            <Autocomplete
              value={formValues.token}
              onChange={(event, newValue) => handleTokenChange(newValue)}
              options={tokensList}
              disabled={editDialog.mode === "edit"}
              getOptionLabel={(option) => option?.token_name || ""}
              isOptionEqualToValue={(option, value) => option.token_id === value.token_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Token Name"
                  placeholder="Type to search tokens..."
                  error={!!validationErrors.token}
                  helperText={validationErrors.token || "Only active tokens without bucket access are listed"}
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <VpnKeyIcon color="action"/>
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="No tokens available"
            />
            <Autocomplete
              multiple
              value={formValues.bucket_names || []}
              onChange={(event, newValue) => handleFormChange("bucket_names", newValue)}
              options={getBucketOptions()}
              disableCloseOnSelect
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({index})}
                    key={option}
                    icon={<StorageIcon sx={{fontSize: 16}}/>}
                    label={option}
                    size="small"
                    sx={PORTAL_BUCKET_CHIP}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buckets"
                  placeholder="Select one or more buckets..."
                  error={!!validationErrors.bucket_names}
                  helperText={validationErrors.bucket_names || "A single bucket can be assigned to multiple tokens"}
                  required
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{display: "flex", alignItems: "center", gap: 1}}>
                  <StorageIcon sx={{fontSize: 18, color: PORTAL_PRIMARY}}/>
                  <Typography variant="body2">{option}</Typography>
                </Box>
              )}
              noOptionsText="No buckets available"
              ListboxProps={{sx: {maxHeight: 200}}}
            />
          </Stack>
        </DialogContent>
        <Divider/>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button variant="outline-secondary" onClick={closeDialog}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={isPendingData}
                  style={{display: "flex", alignItems: "center", gap: 6}}>
            <SaveIcon fontSize="small"/>
            {isPendingData ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={!!deleteTargetRow} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogContent>
          <DeleteConfirmation
            title={<>Are you sure you want to delete bucket access of token
              - <strong>{deleteTargetRow?.token_name ?? ""}</strong>?</>}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </DialogContent>
      </Dialog>
      
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast}
                anchorOrigin={{vertical: "top", horizontal: "center"}}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled"
               sx={{width: "100%", borderRadius: 2}}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
